/**
 * LmsCourseDetail — admin course editor.
 * Shows modules with nested lessons; embedded quizzes; AI refine dialog;
 * enrollments tab.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Plus, Save, Trash2, ChevronDown, ChevronRight, HelpCircle, Users, Send, Award, PlayCircle } from "lucide-react";

export default function LmsCourseDetail() {
  const { id } = useParams();
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [tab, setTab] = useState("content"); // content | enrollments
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  // Refine dialog
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineTarget, setRefineTarget] = useState({ target: "course", target_id: null, label: "Whole course" });
  const [refineText, setRefineText] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  // Enroll
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [enrollUserIds, setEnrollUserIds] = useState([]);
  const [enrollMandatory, setEnrollMandatory] = useState(false);
  const [enrollments, setEnrollments] = useState([]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/courses/${id}`);
      setCourse(data);
      if (data.modules?.length && !expandedModule) setExpandedModule(data.modules[0].id);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId, id, expandedModule]);

  useEffect(() => { load(); }, [load]);

  const loadEnrollments = async () => {
    try {
      const [{ data: e }, { data: m }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/enrollments`),
        api.get(`/workspaces/${activeWorkspaceId}/members`),
      ]);
      setEnrollments(Array.isArray(e) ? e : []);
      setMembers(Array.isArray(m) ? m : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load enrollments failed")); }
  };

  useEffect(() => { if (tab === "enrollments") loadEnrollments(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (patchObj) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/lms/courses/${id}`, patchObj);
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const publish = () => patch({ status: course.status === "published" ? "draft" : "published" });

  const addModule = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/modules`, { title: "New module" });
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Add module failed")); }
  };

  const patchModule = async (mid, body) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/lms/modules/${mid}`, body);
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const deleteModule = async (mid) => {
    if (!confirm("Delete this module and all its lessons?")) return;
    try {
      const { data } = await api.delete(`/workspaces/${activeWorkspaceId}/lms/modules/${mid}`);
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const addLesson = async (moduleId) => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/lessons`,
        { module_id: moduleId, title: "New lesson", body_md: "" });
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Add lesson failed")); }
  };

  const patchLesson = async (lid, body) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/lms/lessons/${lid}`, body);
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const deleteLesson = async (lid) => {
    if (!confirm("Delete this lesson?")) return;
    try {
      const { data } = await api.delete(`/workspaces/${activeWorkspaceId}/lms/lessons/${lid}`);
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const addQuiz = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/quizzes`,
        { title: "New quiz", scored: true, pass_percent: 70, prometric_mode: "strict", max_attempts: 2 });
      setCourse(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Add quiz failed")); }
  };

  const generateBody = async (lid) => {
    try {
      toast.info("Generating full body via AI (10-40s)…");
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/lessons/${lid}/generate-body`, {});
      setCourse(data);
      toast.success("Lesson body generated");
    } catch (e) { toast.error(apiErrorMessage(e, "Generate body failed")); }
  };

  const openRefine = (target, target_id, label) => {
    setRefineTarget({ target, target_id, label });
    setRefineText("");
    setRefineOpen(true);
  };

  const runRefine = async () => {
    if (!refineText.trim()) return;
    setRefineBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/refine`,
        { target: refineTarget.target, target_id: refineTarget.target_id || id, instruction: refineText });
      setCourse(data);
      setRefineOpen(false);
      toast.success("Refined via AI");
    } catch (e) { toast.error(apiErrorMessage(e, "AI refine failed")); }
    finally { setRefineBusy(false); }
  };

  const doEnroll = async () => {
    if (!enrollUserIds.length) return toast.error("Pick at least one user");
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/lms/courses/${id}/enroll`,
        { user_ids: enrollUserIds, source: "direct", mandatory: enrollMandatory });
      toast.success(`Enrolled ${enrollUserIds.length} user(s)`);
      setEnrollOpen(false);
      setEnrollUserIds([]);
      loadEnrollments();
    } catch (e) { toast.error(apiErrorMessage(e, "Enroll failed")); }
  };

  if (!course) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={course.title}
        description={`${course.category || "Uncategorised"} · ${course.status}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="rounded-none h-9" onClick={() => openRefine("course", null, "Whole course header")} data-testid="lms-refine-course">
              <Sparkles className="w-4 h-4 mr-1" /> AI refine
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setEnrollOpen(true)} data-testid="lms-enroll-open">
              <Users className="w-4 h-4 mr-1" /> Enroll users
            </Button>
            <Button className="rounded-none h-9" onClick={publish} data-testid="lms-publish">
              <Send className="w-4 h-4 mr-1" /> {course.status === "published" ? "Unpublish" : "Publish"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <aside className="border border-border p-3 space-y-3">
          <div>
            <Label className="text-[10px] label-eyebrow">Title</Label>
            <Input defaultValue={course.title} onBlur={(e) => e.target.value !== course.title && patch({ title: e.target.value })} className="h-8 rounded-none" data-testid="lms-course-title" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Description</Label>
            <Textarea defaultValue={course.description || ""} onBlur={(e) => (e.target.value || "") !== (course.description || "") && patch({ description: e.target.value })} rows={4} className="rounded-none text-xs" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Category</Label>
            <Input defaultValue={course.category || ""} onBlur={(e) => (e.target.value || "") !== (course.category || "") && patch({ category: e.target.value })} className="h-8 rounded-none" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Pass %</Label>
            <Input type="number" min={1} max={100} defaultValue={course.pass_percent} onBlur={(e) => Number(e.target.value) !== course.pass_percent && patch({ pass_percent: Number(e.target.value) })} className="h-8 rounded-none" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs">
            <Switch checked={course.cert_enabled || false} onCheckedChange={(v) => patch({ cert_enabled: v })} data-testid="lms-cert-toggle" />
            <Award className="w-3 h-3" /> Certificate on completion
          </label>
        </aside>

        <div className="space-y-4">
          <div className="border-b border-border flex gap-1">
            {[["content", `Content (${course.modules?.length || 0} modules)`],
                ["enrollments", `Enrollments (${course.enrollment_count || 0})`]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === v ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid={`lms-tab-${v}`}>{l}</button>
            ))}
          </div>

          {tab === "content" && (
            <div className="space-y-3">
              {(course.modules || []).map((m) => (
                <div key={m.id} className="border border-border" data-testid={`lms-module-${m.id}`}>
                  <div className="flex items-center gap-2 p-2 bg-muted/30">
                    <button onClick={() => setExpandedModule(expandedModule === m.id ? null : m.id)} className="text-muted-foreground">
                      {expandedModule === m.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <Input defaultValue={m.title} onBlur={(e) => e.target.value !== m.title && patchModule(m.id, { title: e.target.value, description: m.description })} className="h-7 rounded-none text-sm font-semibold" data-testid={`lms-module-${m.id}-title`} />
                    <span className="text-[10px] text-muted-foreground">
                      {(course.lessons || []).filter((l) => l.module_id === m.id).length} lessons
                    </span>
                    <div className="flex-1" />
                    <button onClick={() => openRefine("module", m.id, m.title)} className="text-primary" title="AI refine">
                      <Sparkles className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteModule(m.id)} className="text-rose-600" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {expandedModule === m.id && (
                    <div className="p-2 space-y-2">
                      {(course.lessons || []).filter((l) => l.module_id === m.id).map((l) => (
                        <div key={l.id} className="border border-border" data-testid={`lms-lesson-${l.id}`}>
                          <div className="flex items-center gap-2 p-2">
                            <button onClick={() => setExpandedLesson(expandedLesson === l.id ? null : l.id)} className="text-muted-foreground">
                              {expandedLesson === l.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <Input defaultValue={l.title} onBlur={(e) => e.target.value !== l.title && patchLesson(l.id, { title: e.target.value })} className="h-7 rounded-none text-sm" data-testid={`lms-lesson-${l.id}-title`} />
                            <span className="text-[10px] text-muted-foreground">{l.duration_min || 5}m</span>
                            <div className="flex-1" />
                            <button onClick={() => openRefine("lesson", l.id, l.title)} className="text-primary" title="AI refine body">
                              <Sparkles className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => deleteLesson(l.id)} className="text-rose-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {expandedLesson === l.id && (
                            <div className="p-2 border-t border-border space-y-2">
                              <div className="flex justify-end">
                                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => generateBody(l.id)} data-testid={`lms-lesson-${l.id}-gen-body`}>
                                  <Sparkles className="w-3 h-3 mr-1" /> Generate full body (AI)
                                </Button>
                              </div>
                              <div>
                                <Label className="text-[10px] label-eyebrow">Video URL (YouTube / Loom / Vimeo)</Label>
                                <Input defaultValue={l.video_url || ""} onBlur={(e) => e.target.value !== (l.video_url || "") && patchLesson(l.id, { video_url: e.target.value })} className="h-8 rounded-none text-xs" data-testid={`lms-lesson-${l.id}-video`} />
                              </div>
                              <div>
                                <Label className="text-[10px] label-eyebrow">Body (Markdown)</Label>
                                <Textarea defaultValue={l.body_md || ""} onBlur={(e) => e.target.value !== (l.body_md || "") && patchLesson(l.id, { body_md: e.target.value })} rows={10} className="rounded-none text-xs font-mono" data-testid={`lms-lesson-${l.id}-body`} />
                              </div>
                              {l.image_prompts?.length > 0 && (
                                <div className="border border-dashed border-border p-2 text-[11px]">
                                  <div className="label-eyebrow mb-1">AI-suggested image prompts</div>
                                  {l.image_prompts.map((p, i) => (
                                    <div key={i} className="text-muted-foreground">• {p}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      <Button variant="outline" className="rounded-none w-full h-8" onClick={() => addLesson(m.id)} data-testid={`lms-add-lesson-${m.id}`}>
                        <Plus className="w-3 h-3 mr-1" /> Add lesson
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              <Button variant="outline" className="rounded-none w-full" onClick={addModule} data-testid="lms-add-module">
                <Plus className="w-4 h-4 mr-1" /> Add module
              </Button>

              <div className="mt-6 space-y-2">
                <div className="text-[10px] label-eyebrow text-muted-foreground">Quizzes</div>
                {(course.quizzes || []).map((q) => (
                  <QuizCard key={q.id} quiz={q} wsId={activeWorkspaceId} courseId={id} onChange={load} openRefine={openRefine} />
                ))}
                <Button variant="outline" className="rounded-none w-full h-8" onClick={addQuiz} data-testid="lms-add-quiz">
                  <Plus className="w-3 h-3 mr-1" /> Add quiz
                </Button>
              </div>
            </div>
          )}

          {tab === "enrollments" && (
            <div className="space-y-2">
              {enrollments.length === 0 && (
                <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No enrollments yet. Click <b>Enroll users</b>.
                </div>
              )}
              {enrollments.map((e) => (
                <div key={e.id} className="border border-border p-2 flex items-center gap-2 text-sm" data-testid={`lms-enrollment-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{e.user_name}</div>
                    <div className="text-[10px] text-muted-foreground">{e.user_email}</div>
                  </div>
                  <span className={`chip ${e.mandatory ? "chip-warning" : "chip-info"} text-[9px]`}>{e.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
                  <span className="chip text-[9px]">{e.status}</span>
                  <div className="w-24 h-2 bg-muted rounded-none">
                    <div className="h-2 bg-emerald-500" style={{ width: `${e.progress_percent || 0}%` }} />
                  </div>
                  <span className="text-[10px] w-10 text-right">{e.progress_percent || 0}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Refine dialog */}
      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="lms-refine-dialog">
          <DialogHeader>
            <DialogTitle>AI refine · {refineTarget.label}</DialogTitle>
            <DialogDescription>Free-form instruction. The LLM will rewrite this {refineTarget.target}.</DialogDescription>
          </DialogHeader>
          <Textarea value={refineText} onChange={(e) => setRefineText(e.target.value)} rows={4} className="rounded-none" placeholder="e.g. Make it more technical / add examples / shorten to 400 words" data-testid="lms-refine-text" />
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setRefineOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={runRefine} disabled={refineBusy} data-testid="lms-refine-run">
              {refineBusy ? "Refining…" : "Refine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-lg rounded-none max-h-[85vh] overflow-y-auto" data-testid="lms-enroll-dialog">
          <DialogHeader>
            <DialogTitle>Enroll workspace members</DialogTitle>
          </DialogHeader>
          <label className="inline-flex items-center gap-2 text-xs">
            <Switch checked={enrollMandatory} onCheckedChange={setEnrollMandatory} data-testid="lms-enroll-mandatory" />
            Mandatory
          </label>
          <div className="border border-border max-h-64 overflow-y-auto">
            {members.map((m) => (
              <label key={m.user_id || m.id} className="flex items-center gap-2 p-2 border-b border-border last:border-b-0 text-sm cursor-pointer hover:bg-muted/30" data-testid={`lms-enroll-user-${m.user_id || m.id}`}>
                <input type="checkbox" checked={enrollUserIds.includes(m.user_id || m.id)} onChange={(e) => {
                  const uid = m.user_id || m.id;
                  setEnrollUserIds(e.target.checked ? [...enrollUserIds, uid] : enrollUserIds.filter((x) => x !== uid));
                }} />
                <span className="flex-1">{m.name || m.email}</span>
                <span className="text-[10px] text-muted-foreground">{m.role}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEnrollOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={doEnroll} data-testid="lms-enroll-run">Enroll {enrollUserIds.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuizCard({ quiz, wsId, courseId, onChange, openRefine }) {
  const [expanded, setExpanded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const loadQs = useCallback(async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/lms/quizzes/${quiz.id}/questions`);
      setQuestions(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent */ }
  }, [wsId, quiz.id]);
  useEffect(() => { if (expanded) loadQs(); }, [expanded, loadQs]);

  const patch = async (body) => {
    try {
      await api.patch(`/workspaces/${wsId}/lms/quizzes/${quiz.id}`, body);
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const removeQuiz = async () => {
    if (!confirm("Delete this quiz?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/lms/quizzes/${quiz.id}`);
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border" data-testid={`lms-quiz-${quiz.id}`}>
      <div className="flex items-center gap-2 p-2 bg-amber-50/40 dark:bg-amber-900/10">
        <HelpCircle className="w-4 h-4 text-amber-500" />
        <Input defaultValue={quiz.title} onBlur={(e) => e.target.value !== quiz.title && patch({ title: e.target.value })} className="h-7 rounded-none text-sm font-semibold" data-testid={`lms-quiz-${quiz.id}-title`} />
        <span className="text-[10px] text-muted-foreground">{quiz.question_count} qs</span>
        <div className="flex-1" />
        <button onClick={() => openRefine("quiz", quiz.id, quiz.title)} className="text-primary" title="AI regenerate questions">
          <Sparkles className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <button onClick={removeQuiz} className="text-rose-600">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && (
        <div className="p-2 space-y-2">
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] label-eyebrow">Pass %</Label>
              <Input type="number" min={1} max={100} defaultValue={quiz.pass_percent || 70} onBlur={(e) => Number(e.target.value) !== quiz.pass_percent && patch({ pass_percent: Number(e.target.value) })} className="h-7 rounded-none text-xs" />
            </div>
            <div>
              <Label className="text-[10px] label-eyebrow">Time limit (min)</Label>
              <Input type="number" min={1} defaultValue={quiz.time_limit_min || ""} onBlur={(e) => patch({ time_limit_min: e.target.value ? Number(e.target.value) : null })} className="h-7 rounded-none text-xs" placeholder="—" />
            </div>
            <div>
              <Label className="text-[10px] label-eyebrow">Max attempts</Label>
              <Input type="number" min={1} defaultValue={quiz.max_attempts || 1} onBlur={(e) => Number(e.target.value) !== quiz.max_attempts && patch({ max_attempts: Number(e.target.value) })} className="h-7 rounded-none text-xs" />
            </div>
            <div>
              <Label className="text-[10px] label-eyebrow">Proctor mode</Label>
              <Select value={quiz.prometric_mode || "strict"} onValueChange={(v) => patch({ prometric_mode: v })}>
                <SelectTrigger className="h-7 rounded-none text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strict">Strict</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[10px] label-eyebrow text-muted-foreground pt-1">Questions</div>
          {questions.map((q, i) => (
            <div key={q.id} className="text-xs border-l-2 border-border pl-2">
              <span className="text-muted-foreground font-mono mr-1">#{i + 1}</span>
              <span className="chip chip-info text-[9px] mr-1">{q.type}</span>
              {q.text}
              {q.type !== "short_text" && (
                <div className="text-[10px] text-muted-foreground pl-4 mt-0.5">
                  Correct: {(q.correct_answers || []).join(", ") || "—"}
                </div>
              )}
            </div>
          ))}
          {questions.length === 0 && <div className="text-[11px] text-muted-foreground italic">No questions — use ✦ AI to generate.</div>}
        </div>
      )}
    </div>
  );
}
